import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import useUserPid from '@/features/auth/hooks/use-user-pid';
import type { UserPid } from '@/features/auth/services/oidc';
import { getVerificationInformation, type VerificationInformation } from '@/services/api/profile';

const SUBMISSION_POLL_BASE_INTERVAL_MS = 1_000;
const SUBMISSION_POLL_MAX_INTERVAL_MS = 8_000;
const SUBMISSION_MAX_WAIT_MS = 30_000;

const profileVerificationInformationQueryKey = (pid: UserPid) => ['profile', 'verification-information', pid];

const profileVerificationInformationQueryOptions = (pid: UserPid) =>
  queryOptions({
    enabled: !!pid,
    staleTime: 1000 * 60 * 5, // 5 minutes
    queryFn: getVerificationInformation,
    queryKey: profileVerificationInformationQueryKey(pid),
  });

// Only a stored record carries a date, the status is NOT_VERIFIED with or without one
const hasVerificationRecord = (verificationInformation: VerificationInformation | undefined) => !!verificationInformation?.verification?.verification_date;

// Doubles after each fetch, as a submission that hasn't arrived within a few seconds is likely to be a while
const getSubmissionPollInterval = (fetchCount: number) => Math.min(SUBMISSION_POLL_BASE_INTERVAL_MS * 2 ** Math.max(fetchCount - 1, 0), SUBMISSION_POLL_MAX_INTERVAL_MS);

// Its own key, so data written through set before the server has a record can't end the wait
const profileVerificationSubmissionQueryOptions = (pid: UserPid, enabled: boolean, isPolling: boolean) =>
  queryOptions({
    enabled: !!pid && enabled,
    staleTime: 0,
    queryFn: getVerificationInformation,
    queryKey: [...profileVerificationInformationQueryKey(pid), 'submission'],
    refetchInterval: (query) => (isPolling && !hasVerificationRecord(query.state.data) ? getSubmissionPollInterval(query.state.dataUpdateCount) : false),
  });

const useProfileVerificationInformation = () => {
  const queryClient = useQueryClient();
  const { pid } = useUserPid();
  const queryResult = useQuery(profileVerificationInformationQueryOptions(pid));

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: profileVerificationInformationQueryKey(pid) });
  }, [pid, queryClient]);

  const set = useCallback(
    (verificationInformation: Partial<VerificationInformation>) => {
      queryClient.setQueryData(profileVerificationInformationQueryKey(pid), (previousVerificationInformation: VerificationInformation) => ({
        ...previousVerificationInformation,
        ...verificationInformation,
      }));
    },
    [pid, queryClient],
  );

  return {
    ...queryResult,
    invalidate,
    set,
  };
};

// Sumsub's webhook reaches the server a moment after the user submits
const useProfileVerificationSubmissionWait = (enabled = true) => {
  const { pid } = useUserPid();
  const [hasStoppedWaiting, setHasStoppedWaiting] = useState(false);
  const { data } = useQuery(profileVerificationSubmissionQueryOptions(pid, enabled, !hasStoppedWaiting));

  useEffect(() => {
    if (!enabled) return;

    const timeout = setTimeout(() => setHasStoppedWaiting(true), SUBMISSION_MAX_WAIT_MS);

    return () => clearTimeout(timeout);
  }, [enabled]);

  return {
    isWaiting: enabled && !hasStoppedWaiting && !hasVerificationRecord(data),
  };
};

export { SUBMISSION_MAX_WAIT_MS, SUBMISSION_POLL_BASE_INTERVAL_MS, SUBMISSION_POLL_MAX_INTERVAL_MS, useProfileVerificationInformation, useProfileVerificationSubmissionWait };
